/**
 * Textos es-PE del Admin (T031, implementation-notes §4).
 *
 * Un JSON por feature en `src/i18n/es-PE/<feature>.json`; el nombre del archivo es el espacio de
 * nombres de la clave: `common.json` → `t('common.buttons.save')`. No edites el JSON de otra
 * feature. Las claves ausentes devuelven la propia clave para que el hueco se vea en pantalla.
 */
export const LOCALE = 'es-PE';

export type TranslationVars = Record<string, string | number>;

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

const modules = import.meta.glob<Record<string, JsonValue>>('./es-PE/*.json', {
  eager: true,
  import: 'default',
});

function flatten(value: JsonValue, prefix: string, target: Map<string, string>): void {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    target.set(prefix, String(value));
    return;
  }
  for (const [key, child] of Object.entries(value)) {
    flatten(child, prefix ? `${prefix}.${key}` : key, target);
  }
}

function buildDictionary(): Map<string, string> {
  const dictionary = new Map<string, string>();
  for (const [path, content] of Object.entries(modules)) {
    const namespace = path.replace(/^.*\//, '').replace(/\.json$/, '');
    flatten(content as JsonValue, namespace, dictionary);
  }
  return dictionary;
}

const dictionary = buildDictionary();

/** Todas las claves disponibles (útil en pruebas). */
export function translationKeys(): string[] {
  return [...dictionary.keys()].sort();
}

/** Reemplaza `{variable}` por su valor; deja el marcador intacto si no se pasó la variable. */
export function interpolate(template: string, vars?: TranslationVars): string {
  if (!vars) {
    return template;
  }
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = vars[name];
    return value === undefined ? match : String(value);
  });
}

/** Texto para una clave `namespace.ruta.a.la.clave`. */
export function t(key: string, vars?: TranslationVars): string {
  const template = dictionary.get(key);
  if (template === undefined) {
    return key;
  }
  return interpolate(template, vars);
}

// ---------------------------------------------------------------------------
// Fechas y horas: siempre es-PE, 24 h, en la zona indicada (la de la empresa o la sede).
// ---------------------------------------------------------------------------

function formatter(options: Intl.DateTimeFormatOptions, timeZone?: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(LOCALE, {
    hour12: false,
    ...(timeZone ? { timeZone } : {}),
    ...options,
  });
}

/** `2026-09-17T13:05:00Z` + `America/Lima` → `17/09/2026, 08:05`. */
export function formatDateTime(iso: string, timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return formatter(
    {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    },
    timeZone,
  ).format(date);
}

/** `2026-09-17T13:05:00Z` + `America/Lima` → `17/09/2026`. */
export function formatDate(iso: string, timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return formatter({ year: 'numeric', month: '2-digit', day: '2-digit' }, timeZone).format(date);
}

/** `2026-09-17T13:05:00Z` + `America/Lima` → `08:05`. */
export function formatTime(iso: string, timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return formatter({ hour: '2-digit', minute: '2-digit' }, timeZone).format(date);
}
